import { useState, useEffect } from "react";
import instance from "api/globalaxios";

// Cache for states: { [countryCode]: [states] }
const statesCache = {};
// Cache for cities: { [`${countryCode}_${stateCode}`]: [cities] }
const citiesCache = {};

export async function fetchStates(countryCode) {
  if (!countryCode) return [];
  const normalizedCode = countryCode.toUpperCase();
  if (statesCache[normalizedCode]) {
    return statesCache[normalizedCode];
  }
  try {
    const response = await instance.get("bac/locations/", {
      params: { country_code: normalizedCode },
    });
    if (response.data && response.data.success) {
      const statesList = response.data.states || [];
      statesCache[normalizedCode] = statesList;
      return statesList;
    }
    return [];
  } catch (error) {
    console.error("[useLocations] Failed to fetch states:", error);
    return [];
  }
}

export async function fetchCities(countryCode, stateCode) {
  if (!countryCode || !stateCode) return [];
  const normalizedCountry = countryCode.toUpperCase();
  const normalizedState = stateCode.toUpperCase();
  const cacheKey = `${normalizedCountry}_${normalizedState}`;
  if (citiesCache[cacheKey]) {
    return citiesCache[cacheKey];
  }
  try {
    const response = await instance.get("bac/locations/", {
      params: {
        country_code: normalizedCountry,
        state_code: normalizedState,
      },
    });
    if (response.data && response.data.success) {
      const citiesList = response.data.cities || [];
      citiesCache[cacheKey] = citiesList;
      return citiesList;
    }
    return [];
  } catch (error) {
    console.error("[useLocations] Failed to fetch cities:", error);
    return [];
  }
}

export default function useLocations(countryCode, stateCode) {
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);

  useEffect(() => {
    let active = true;
    if (!countryCode) {
      setStates([]);
      return;
    }
    setLoadingStates(true);
    fetchStates(countryCode).then((list) => {
      if (active) {
        setStates(list);
        setLoadingStates(false);
      }
    });
    return () => {
      active = false;
    };
  }, [countryCode]);

  useEffect(() => {
    let active = true;
    if (!countryCode || !stateCode) {
      setCities([]);
      return;
    }
    setLoadingCities(true);
    fetchCities(countryCode, stateCode).then((list) => {
      if (active) {
        setCities(list);
        setLoadingCities(false);
      }
    });
    return () => {
      active = false;
    };
  }, [countryCode, stateCode]);

  return { states, cities, loadingStates, loadingCities };
}
